import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Badge,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stat,
  StatLabel,
  StatNumber,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaSearch, FaPlus, FaMinus, FaUndo, FaBoxOpen } from 'react-icons/fa';

/**
 * Phlebotomy supply inventory.
 *
 * Counts persist to localStorage so a quick stock-count survives a refresh.
 * Each item has a `par` (the level you want to keep on hand); anything at or
 * below `low` flags as "Low" and zero flags as "Out".
 */

const STORAGE_KEY = 'phleb_inventory_v1';

// cap = the physical tube-cap color, rendered as a swatch so the list reads
// the way the supply drawer looks.
const DEFAULT_ITEMS = [
  // --- Collection tubes ---
  { id: 'royal-blue', name: 'Royal Blue', detail: 'Trace element / metals', group: 'Collection Tubes', cap: '#1e40af', qty: 0, par: 25, low: 8 },
  { id: 'green-sodium', name: 'Green — Sodium Heparin', detail: 'Plasma, chemistry', group: 'Collection Tubes', cap: '#15803d', qty: 0, par: 25, low: 8 },
  { id: 'green-lithium', name: 'Green — Lithium Heparin', detail: 'Plasma, chemistry', group: 'Collection Tubes', cap: '#22c55e', qty: 0, par: 25, low: 8 },
  { id: 'lavender', name: 'Lavender (Lav)', detail: 'EDTA — hematology / CBC', group: 'Collection Tubes', cap: '#a855f7', qty: 0, par: 30, low: 10 },
  { id: 'sst', name: 'SST', detail: 'Serum separator — gold top', group: 'Collection Tubes', cap: '#ca8a04', qty: 0, par: 30, low: 10 },

  // --- Swabs ---
  { id: 'eswabs', name: 'ESwabs', detail: 'Bacterial / culture collection', group: 'Swabs', cap: '#ec4899', qty: 0, par: 20, low: 6 },
  { id: 'utm-swabs', name: 'UTM Swabs', detail: 'Universal transport media — viral', group: 'Swabs', cap: '#0ea5e9', qty: 0, par: 20, low: 6 },

  // --- Consumables ---
  { id: 'gloves', name: 'Gloves', detail: 'Nitrile, exam', group: 'Consumables', cap: '#64748b', qty: 0, par: 5, low: 2 },
  { id: 'tourniquet', name: 'Tourniquet', detail: 'Latex-free', group: 'Consumables', cap: '#64748b', qty: 0, par: 10, low: 3 },
  { id: 'band-aids', name: 'Band-Aids', detail: 'Adhesive bandages', group: 'Consumables', cap: '#64748b', qty: 0, par: 3, low: 1 },
  { id: 'alcohol', name: 'Alcohol Prep Pads', detail: '70% isopropyl', group: 'Consumables', cap: '#64748b', qty: 0, par: 5, low: 2 },
  { id: 'gauze-pads', name: 'Gauze Pads', detail: '2x2 sterile', group: 'Consumables', cap: '#64748b', qty: 0, par: 5, low: 2 },
];

const GROUP_ORDER = ['Collection Tubes', 'Swabs', 'Consumables'];

function statusFor(item) {
  if (item.qty <= 0) return { label: 'Out', scheme: 'red' };
  if (item.qty <= item.low) return { label: 'Low', scheme: 'orange' };
  return { label: 'In stock', scheme: 'green' };
}

function Inventory() {
  const cardBg = useColorModeValue('white', 'gray.700');
  const border = useColorModeValue('gray.200', 'gray.600');
  const subtle = useColorModeValue('gray.500', 'gray.400');

  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [search, setSearch] = useState('');

  // Load saved counts on mount, merged onto the canonical item list so new
  // default items still show up even if an old payload is in storage.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && Array.isArray(saved)) {
        const byId = Object.fromEntries(saved.map((s) => [s.id, s.qty]));
        setItems((prev) => prev.map((it) => ({ ...it, qty: byId[it.id] ?? it.qty })));
      }
    } catch (e) {
      /* ignore malformed storage */
    }
  }, []);

  const persist = (next) => {
    setItems(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next.map(({ id, qty }) => ({ id, qty }))));
    } catch (e) {
      /* storage full / unavailable — counts stay in memory */
    }
  };

  const bump = (id, delta) =>
    persist(items.map((it) => (it.id === id ? { ...it, qty: Math.max(0, it.qty + delta) } : it)));

  const setQty = (id, value) => {
    const n = Math.max(0, parseInt(value, 10) || 0);
    persist(items.map((it) => (it.id === id ? { ...it, qty: n } : it)));
  };

  const resetAll = () => persist(items.map((it) => ({ ...it, qty: 0 })));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) => it.name.toLowerCase().includes(q) || it.detail.toLowerCase().includes(q)
    );
  }, [items, search]);

  const stats = useMemo(() => {
    const total = items.reduce((sum, it) => sum + it.qty, 0);
    const out = items.filter((it) => it.qty <= 0).length;
    const low = items.filter((it) => it.qty > 0 && it.qty <= it.low).length;
    return { total, out, low, kinds: items.length };
  }, [items]);

  return (
    <Box maxW="1100px" mx="auto">
      {/* Header */}
      <Flex align="center" justify="space-between" mb={6} wrap="wrap" gap={3}>
        <Box>
          <Flex align="center" gap={3}>
            <Box
              bg="blue.600"
              color="white"
              boxSize="44px"
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontSize="20px"
            >
              <FaBoxOpen />
            </Box>
            <Box>
              <Heading size="lg" color="blue.700">Supply Inventory</Heading>
              <Text fontSize="sm" color={subtle}>Phlebotomy draw kit — tubes, swabs &amp; consumables</Text>
            </Box>
          </Flex>
        </Box>
        <Button leftIcon={<FaUndo />} variant="outline" size="sm" onClick={resetAll}>
          Reset counts
        </Button>
      </Flex>

      {/* Stat strip */}
      <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={4} mb={6}>
        {[
          { label: 'Item types', value: stats.kinds, color: 'blue.600' },
          { label: 'Total units', value: stats.total, color: 'gray.700' },
          { label: 'Low stock', value: stats.low, color: 'orange.500' },
          { label: 'Out of stock', value: stats.out, color: 'red.500' },
        ].map((s) => (
          <Stat key={s.label} bg={cardBg} borderWidth="1px" borderColor={border} borderRadius="lg" px={4} py={3} boxShadow="sm">
            <StatLabel fontSize="xs" color={subtle} textTransform="uppercase" letterSpacing="wide">{s.label}</StatLabel>
            <StatNumber color={s.color}>{s.value}</StatNumber>
          </Stat>
        ))}
      </Grid>

      {/* Search */}
      <InputGroup maxW="360px" mb={6}>
        <InputLeftElement pointerEvents="none"><FaSearch color="#A0AEC0" /></InputLeftElement>
        <Input
          placeholder="Search supplies…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          bg={cardBg}
        />
      </InputGroup>

      {/* Groups */}
      {GROUP_ORDER.map((group) => {
        const groupItems = filtered.filter((it) => it.group === group);
        if (groupItems.length === 0) return null;
        return (
          <Box key={group} mb={8}>
            <Flex align="center" gap={2} mb={3}>
              <Heading size="sm" color="gray.600" textTransform="uppercase" letterSpacing="wide">
                {group}
              </Heading>
              <Badge colorScheme="blue" borderRadius="full">{groupItems.length}</Badge>
            </Flex>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
              {groupItems.map((item) => {
                const status = statusFor(item);
                return (
                  <Flex
                    key={item.id}
                    align="center"
                    bg={cardBg}
                    borderWidth="1px"
                    borderColor={border}
                    borderLeftWidth="5px"
                    borderLeftColor={item.cap}
                    borderRadius="lg"
                    p={3}
                    gap={3}
                    boxShadow="sm"
                    transition="all 0.15s"
                    _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}
                  >
                    {/* Cap swatch */}
                    <Tooltip label="Cap color" hasArrow>
                      <Box boxSize="26px" borderRadius="full" bg={item.cap} flexShrink={0} borderWidth="1px" borderColor="blackAlpha.200" />
                    </Tooltip>

                    {/* Name + detail */}
                    <Box flex={1} minW={0}>
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold" noOfLines={1}>{item.name}</Text>
                        <Badge colorScheme={status.scheme} fontSize="0.65rem">{status.label}</Badge>
                      </Flex>
                      <Text fontSize="xs" color={subtle} noOfLines={1}>{item.detail} · par {item.par}</Text>
                    </Box>

                    {/* Stepper */}
                    <Flex align="center" gap={1} flexShrink={0}>
                      <IconButton aria-label={`Decrease ${item.name}`} icon={<FaMinus />} size="xs" variant="ghost" onClick={() => bump(item.id, -1)} isDisabled={item.qty <= 0} />
                      <Input
                        value={item.qty}
                        onChange={(e) => setQty(item.id, e.target.value)}
                        size="sm"
                        w="56px"
                        textAlign="center"
                        type="number"
                        fontWeight="bold"
                      />
                      <IconButton aria-label={`Increase ${item.name}`} icon={<FaPlus />} size="xs" variant="ghost" colorScheme="blue" onClick={() => bump(item.id, 1)} />
                    </Flex>
                  </Flex>
                );
              })}
            </Grid>
          </Box>
        );
      })}

      {filtered.length === 0 && (
        <Box textAlign="center" py={12} color={subtle}>
          <Text>No supplies match “{search}”.</Text>
        </Box>
      )}
    </Box>
  );
}

export default Inventory;
